/**
 * Shared pt-BR-aware number formatting (ADR 288) — the first shared helper for
 * what a dozen pages hand-roll as `toFixed(n).replace(".", ",")`. New code
 * should use these; the old copies migrate opportunistically.
 *
 * Locale comes from the ACTIVE i18n language, never hardcoded: "13.035" vs
 * "13,035" reads as a different number in the wrong locale (see the
 * WidgetCard warning).
 */

/** "42,08" — fixed decimals in the given language ("pt-BR" default comma). */
export function fmtNum(v: number, lang: string, digits = 1): string {
  return v.toLocaleString(lang, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** "R$ 1.234,56" — currency in the given language; null-safe ("—"). */
export function fmtMoney(v: number | null | undefined, currency: string, lang: string): string {
  if (v == null) return "—";
  try {
    return v.toLocaleString(lang, { style: "currency", currency });
  } catch {
    // An unknown ISO code must not crash a report page.
    return `${currency} ${fmtNum(v, lang, 2)}`;
  }
}

/** Milliseconds as decimal hours: "12,5 h". */
export function fmtHours(ms: number, lang: string): string {
  return `${fmtNum(ms / 3_600_000, lang, 1)} h`;
}

/** Metres as km when large: "1,2 km" / "850 m". */
export function fmtDistance(m: number, lang: string): string {
  return m >= 1000 ? `${fmtNum(m / 1000, lang, 1)} km` : `${fmtNum(m, lang, 0)} m`;
}

/** "37,5%" — a 0–100 value; null-safe ("—"): unmeasured is absent, never 0. */
export function fmtPct100(v: number | null | undefined, lang: string): string {
  return v == null ? "—" : `${fmtNum(v, lang, 1)}%`;
}

/** Parse a decimal-comma-or-dot text input; NaN on garbage, null on blank. */
export function parseDecimal(s: string): number | null {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  return Number(trimmed.replace(",", "."));
}
